
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, PlaneTakeoff, FileCode2, Code, Shield, Rocket, BarChart3 } from 'lucide-react';

// Phase data with tech-focused details
const phaseData = [
  {
    id: 1,
    title: 'Discovery',
    icon: PlaneTakeoff,
    duration: '1-2 weeks',
    color: '#9b87f5',
    description: 'We analyze requirements and create a technical roadmap.',
  },
  {
    id: 2,
    title: 'Design',
    icon: FileCode2,
    duration: '2-4 weeks',
    color: '#33C3F0',
    description: 'We design user-friendly interfaces with modern tech.',
  },
  {
    id: 3,
    title: 'Development',
    icon: Code,
    duration: '4-12 weeks',
    color: '#0EA5E9',
    description: 'We build scalable solutions with clean architecture.',
  },
  {
    id: 4,
    title: 'Testing',
    icon: Shield,
    duration: '1-2 weeks',
    color: '#6E59A5',
    description: 'We ensure quality through automated and manual tests.',
  },
  {
    id: 5,
    title: 'Deployment',
    icon: Rocket,
    duration: '1 week',
    color: '#F97316',
    description: 'We launch your product with CI/CD pipelines.',
  },
  {
    id: 6,
    title: 'Growth',
    icon: BarChart3,
    duration: 'Ongoing',
    color: '#10B981',
    description: 'We enhance and scale your product for growth.',
  }
];

interface ProcessPhasesProps {
  activePhase: number;
  setActivePhase: (index: number) => void;
}

const ProcessPhases: React.FC<ProcessPhasesProps> = ({ activePhase, setActivePhase }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
      {phaseData.map((phase, index) => (
        <motion.div
          key={phase.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          onClick={() => setActivePhase(index)}
          className={`
            relative cursor-pointer rounded-lg border p-4 md:p-5
            transition-all duration-300 
            ${activePhase === index 
              ? 'bg-white/10 border-[color:var(--color)] shadow-lg transform -translate-y-1' 
              : 'bg-white/5 border-white/10 hover:bg-white/10 hover:-translate-y-1'}
          `}
          style={{ 
            '--color': phase.color,
            boxShadow: activePhase === index ? `0 8px 24px -12px ${phase.color}40` : 'none'
          } as React.CSSProperties}
        >
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div 
              className="p-2 rounded-md mt-1"
              style={{ backgroundColor: `${phase.color}20` }}
            >
              <phase.icon size={20} color={phase.color} />
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h4 className="text-white font-medium">{phase.title}</h4>
                {activePhase === index && (
                  <CheckCircle2 size={16} className="text-[color:var(--color)]" />
                )}
              </div>
              
              <div 
                className="px-2 py-1 rounded text-xs inline-block mt-2"
                style={{ backgroundColor: `${phase.color}20`, color: phase.color }}
              >
                {phase.duration}
              </div>
              
              <p className="mt-2 text-sm text-gray-300">
                {phase.description}
              </p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default ProcessPhases;
